/**
 * ThemeProvider — context wiring for display preferences.
 *
 * - Reads initial prefs from localStorage on mount.
 * - Applies data-* attributes to <html> on mount and on every change.
 * - theme=system: attaches a prefers-color-scheme media-query listener;
 *   removes it when the theme changes away from "system".
 * - Provides set() and reset() to consumers via context.
 *
 * Server persistence:
 *   This component lives in shared/ and is reused by the public (sessionless)
 *   register bundle, so it MUST stay surface-agnostic: it does NOT fetch, does
 *   NOT know any endpoint URL, and does NOT read CSRF tokens. Instead it accepts
 *   an optional `onPersist(prefs)` callback and invokes it whenever preferences
 *   change. Authenticated surfaces (portal, admin) pass an onPersist that issues
 *   the actual request; the sessionless register surface passes none
 *   (localStorage only).
 */
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react'
import {
  DEFAULTS,
  LS_PREFIX,
  resolve,
  type DisplayPreferences,
  ALLOWED_ACCENTS,
  ALLOWED_CONTRASTS,
  ALLOWED_CRISPS,
  ALLOWED_DENSITIES,
  ALLOWED_FONTS,
  ALLOWED_MOTIONS,
  ALLOWED_TEXT_SIZES,
  ALLOWED_THEMES,
  ALLOWED_SIDEBAR_STATES,
  ALLOWED_CASE_WORK_MODES,
  clampSidebarWidth,
  clampCaseColumnWidth,
  clampColorIntensity,
  colorIntensityFilter,
} from './displayPreferences'

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * Callback a surface supplies to persist changed preferences server-side.
 * Takes a partial set so a single change and a full reset use the same seam.
 */
export type PersistDisplayPreferences = (prefs: Partial<DisplayPreferences>) => void

export interface DisplayPreferencesContextValue {
  /** Fully-resolved (never partial) current preferences. */
  prefs: DisplayPreferences
  /**
   * Update a single preference key. Applies data-* to <html> and persists to
   * localStorage; if the surface supplied `onPersist`, that is invoked too.
   */
  set<K extends keyof DisplayPreferences>(key: K, value: DisplayPreferences[K]): void
  /**
   * Reset preferences to DEFAULTS. Clears the matching localStorage keys and,
   * if the surface supplied `onPersist`, writes those defaults server-side too.
   * Pass the keys to reset when the surface hides some of the controls; with no
   * argument every preference is reset.
   */
  reset(keys?: Array<keyof DisplayPreferences>): void
}

export const DisplayPreferencesContext = createContext<DisplayPreferencesContextValue | null>(null)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read all display preferences from localStorage, returning a partial object. */
function readFromStorage(): Partial<DisplayPreferences> {
  const partial: Partial<DisplayPreferences> = {}
  try {
    const theme = localStorage.getItem(`${LS_PREFIX}theme`)
    if (theme && ALLOWED_THEMES.includes(theme as DisplayPreferences['theme'])) {
      partial.theme = theme as DisplayPreferences['theme']
    }
    const accent = localStorage.getItem(`${LS_PREFIX}accent`)
    if (accent && ALLOWED_ACCENTS.includes(accent as DisplayPreferences['accent'])) {
      partial.accent = accent as DisplayPreferences['accent']
    }
    const colorIntensity = localStorage.getItem(`${LS_PREFIX}colorIntensity`)
    if (colorIntensity !== null) partial.colorIntensity = clampColorIntensity(colorIntensity)
    const font = localStorage.getItem(`${LS_PREFIX}font`)
    if (font && ALLOWED_FONTS.includes(font as DisplayPreferences['font'])) {
      partial.font = font as DisplayPreferences['font']
    }
    const textSize = localStorage.getItem(`${LS_PREFIX}textSize`)
    if (textSize && ALLOWED_TEXT_SIZES.includes(textSize as DisplayPreferences['textSize'])) {
      partial.textSize = textSize as DisplayPreferences['textSize']
    }
    const density = localStorage.getItem(`${LS_PREFIX}density`)
    if (density && ALLOWED_DENSITIES.includes(density as DisplayPreferences['density'])) {
      partial.density = density as DisplayPreferences['density']
    }
    const contrast = localStorage.getItem(`${LS_PREFIX}contrast`)
    if (contrast && ALLOWED_CONTRASTS.includes(contrast as DisplayPreferences['contrast'])) {
      partial.contrast = contrast as DisplayPreferences['contrast']
    }
    const crisp = localStorage.getItem(`${LS_PREFIX}crisp`)
    if (crisp && ALLOWED_CRISPS.includes(crisp as DisplayPreferences['crisp'])) {
      partial.crisp = crisp as DisplayPreferences['crisp']
    }
    const motion = localStorage.getItem(`${LS_PREFIX}motion`)
    if (motion && ALLOWED_MOTIONS.includes(motion as DisplayPreferences['motion'])) {
      partial.motion = motion as DisplayPreferences['motion']
    }
    const sidebar = localStorage.getItem(`${LS_PREFIX}sidebar`)
    if (sidebar && ALLOWED_SIDEBAR_STATES.includes(sidebar as DisplayPreferences['sidebar'])) {
      partial.sidebar = sidebar as DisplayPreferences['sidebar']
    }
    const caseWorkMode = localStorage.getItem(`${LS_PREFIX}caseWorkMode`)
    if (caseWorkMode && ALLOWED_CASE_WORK_MODES.includes(caseWorkMode as DisplayPreferences['caseWorkMode'])) {
      partial.caseWorkMode = caseWorkMode as DisplayPreferences['caseWorkMode']
    }
    const sidebarWidth = localStorage.getItem(`${LS_PREFIX}sidebarWidth`)
    if (sidebarWidth) {
      partial.sidebarWidth = clampSidebarWidth(sidebarWidth)
    }
    const caseLeftWidth = localStorage.getItem(`${LS_PREFIX}caseLeftWidth`)
    if (caseLeftWidth) {
      partial.caseLeftWidth = clampCaseColumnWidth(caseLeftWidth, DEFAULTS.caseLeftWidth)
    }
    const caseRightWidth = localStorage.getItem(`${LS_PREFIX}caseRightWidth`)
    if (caseRightWidth) {
      partial.caseRightWidth = clampCaseColumnWidth(caseRightWidth, DEFAULTS.caseRightWidth)
    }
  } catch {
    // localStorage unavailable (SSR, private browsing edge cases) — ignore.
  }
  return partial
}

/** Write a single key to localStorage. */
function writeToStorage(key: keyof DisplayPreferences, value: string | number): void {
  try {
    localStorage.setItem(`${LS_PREFIX}${key}`, String(value))
  } catch {
    // ignore write failures
  }
}

/** Every preference key, in declaration order — the default scope of reset(). */
const ALL_KEYS = Object.keys(DEFAULTS) as Array<keyof DisplayPreferences>

/** Remove the given display-preference keys from localStorage. */
function clearStorage(keys: Array<keyof DisplayPreferences>): void {
  try {
    for (const k of keys) {
      localStorage.removeItem(`${LS_PREFIX}${k}`)
    }
  } catch {
    // ignore
  }
}

/**
 * Apply a fully-resolved DisplayPreferences to <html> data-* attributes.
 * For theme=system the caller must have already resolved to 'light' | 'dark'.
 */
function applyToHtml(prefs: DisplayPreferences, resolvedTheme: 'light' | 'dark'): void {
  const h = document.documentElement
  h.setAttribute('data-theme', resolvedTheme)
  h.setAttribute('data-color-scheme', resolvedTheme)
  h.setAttribute('data-color', prefs.accent === 'authority' ? 'accent' : prefs.accent)
  h.setAttribute('data-size', prefs.density === 'compact' ? 'sm' : prefs.density === 'comfy' ? 'lg' : 'md')
  h.setAttribute('data-accent', prefs.accent)
  h.style.setProperty('--display-color-filter', colorIntensityFilter(prefs.colorIntensity))
  h.setAttribute('data-font', prefs.font)
  h.setAttribute('data-font-scale', prefs.textSize)
  h.setAttribute('data-density', prefs.density)
  h.setAttribute('data-contrast', prefs.contrast)
  h.setAttribute('data-crisp', prefs.crisp)
  h.setAttribute('data-motion', prefs.motion)
  // The sidebar width is a length, not a variant, so it rides on the shell's
  // own custom property instead of a data-* attribute. The collapsed state is
  // a class on the shell root and stays the shell's business.
  h.style.setProperty('--sidebar-w', `${prefs.sidebarWidth}px`)
  // Same reasoning for the case-detail side columns — a length, not a variant.
  h.style.setProperty('--case-left-w', `${prefs.caseLeftWidth}px`)
  h.style.setProperty('--case-right-w', `${prefs.caseRightWidth}px`)
}

/** Query the OS color scheme, returning 'dark' or 'light'. */
function osTheme(): 'light' | 'dark' {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

/**
 * Read server-provided display preferences from the initial Inertia page payload.
 * Inertia 3.x serialises the page into a <script data-page type="application/json">
 * element (2.x used the #app[data-page] attribute, kept here as a fallback). Under
 * inertia-rails 3.x the #app attribute is absent, so reading only it would always
 * miss the payload. ThemeProvider mounts ABOVE the Inertia <App>, so usePage() is
 * unavailable here — we read the DOM payload directly. Absent on sessionless
 * surfaces (register), so server prefs are simply unavailable there.
 */
function readServerPrefs(): Partial<DisplayPreferences> | undefined {
  try {
    const raw =
      document.querySelector('script[data-page][type="application/json"]')?.textContent ??
      document.getElementById('app')?.dataset.page ??
      null
    if (raw) {
      const props = (JSON.parse(raw) as { props?: Record<string, unknown> })?.props
      const dp = props?.['displayPreferences']
      if (dp && typeof dp === 'object') {
        return dp as Partial<DisplayPreferences>
      }
    }
  } catch {
    // No/invalid page payload (sessionless surface, SSR, tests) — localStorage only.
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface ThemeProviderProps {
  children: React.ReactNode
  /**
   * Optional server-provided initial prefs (from Inertia shared prop).
   * When supplied these win over localStorage for the first render.
   * If omitted ThemeProvider reads from the Inertia page prop
   * `displayPreferences` automatically (signed-in users).
   */
  initial?: Partial<DisplayPreferences>
  /**
   * Optional persistence seam. When supplied, the provider invokes it with the
   * changed keys on every set() and with the full defaults on reset(), letting
   * an authenticated surface persist server-side. The provider stays
   * surface-agnostic — it never knows the endpoint, never fetches, never reads
   * CSRF. Sessionless surfaces (register) omit it (localStorage only).
   */
  onPersist?: PersistDisplayPreferences
}

export function ThemeProvider({ children, initial, onPersist }: ThemeProviderProps): React.ReactElement {
  // ThemeProvider mounts ABOVE the Inertia <App>, so usePage() is unavailable
  // here — readServerPrefs() reads the Inertia <script data-page> payload once
  // to seed the initial prefs for a signed-in user. This is a read of an
  // already-rendered DOM payload, not a network fetch.
  const [serverPrefs] = useState(readServerPrefs)

  const [prefs, setPrefs] = useState<DisplayPreferences>(() => {
    const fromStorage = readFromStorage()
    // Priority: explicit prop > Inertia shared prop > localStorage > DEFAULTS
    return resolve({ ...fromStorage, ...serverPrefs, ...initial })
  })

  // Keep the previous prefs in a ref so the declarative apply effect can detach
  // an OS listener bound to a stale prefs object without re-running on identity.
  const prevPrefsRef = useRef<DisplayPreferences | null>(null)

  // Keep onPersist in a ref so set() does not need it in its deps (the surface
  // may pass a fresh closure each render).
  const onPersistRef = useRef(onPersist)
  useEffect(() => {
    onPersistRef.current = onPersist
  })

  // Track the active MediaQueryList for prefers-color-scheme so we can
  // remove the listener when switching away from "system".
  const mqlListenerRef = useRef<{
    mql: MediaQueryList
    fn: (e: MediaQueryListEvent) => void
  } | null>(null)

  /** Remove the current OS-theme listener, if any. */
  const detachOsListener = useCallback(() => {
    if (mqlListenerRef.current) {
      const { mql, fn } = mqlListenerRef.current
      mql.removeEventListener('change', fn)
      mqlListenerRef.current = null
    }
  }, [])

  /** Attach a prefers-color-scheme listener for system theme. */
  const attachOsListener = useCallback(
    (currentPrefs: DisplayPreferences) => {
      detachOsListener()
      try {
        const mql = window.matchMedia('(prefers-color-scheme: dark)')
        const fn = (e: MediaQueryListEvent) => {
          const resolvedTheme = e.matches ? 'dark' : 'light'
          applyToHtml(currentPrefs, resolvedTheme)
        }
        mql.addEventListener('change', fn)
        mqlListenerRef.current = { mql, fn }
      } catch {
        // matchMedia not available
      }
    },
    [detachOsListener],
  )

  // Single declarative apply effect: runs on mount and on every prefs change.
  // Writes data-* to <html>, and manages the OS listener for theme=system.
  useEffect(() => {
    const resolvedTheme = prefs.theme === 'system' ? osTheme() : prefs.theme
    applyToHtml(prefs, resolvedTheme)
    if (prefs.theme === 'system') {
      attachOsListener(prefs)
    } else {
      detachOsListener()
    }
    prevPrefsRef.current = prefs
  }, [prefs, attachOsListener, detachOsListener])

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      detachOsListener()
    }
  }, [detachOsListener])

  const set = useCallback(
    <K extends keyof DisplayPreferences>(key: K, value: DisplayPreferences[K]) => {
      // Pure updater: compute and return the next state only. All side effects
      // (DOM, OS listener, localStorage, server persist) happen outside the
      // updater so React.StrictMode's double-invoke cannot run them twice.
      setPrefs((prev) => ({ ...prev, [key]: value }))

      // Side effects after scheduling the update. The declarative effect above
      // owns data-* + OS-listener reconciliation; here we only persist.
      writeToStorage(key, value)
      onPersistRef.current?.({ [key]: value })
    },
    [],
  )

  const reset = useCallback((keys: Array<keyof DisplayPreferences> = ALL_KEYS) => {
    // Scoped so a surface that hides a control also stops resetting it. The
    // portal hides theme, accent and font and renders one fixed look; wiping
    // them anyway would destroy the choice a dual-role user made on the
    // workstation, where those controls are real. Omitting the argument resets
    // everything, which is what a surface showing every control passes.
    const defaults: Partial<DisplayPreferences> = {}
    for (const key of keys) Object.assign(defaults, { [key]: DEFAULTS[key] })

    clearStorage(keys)
    setPrefs((prev) => ({ ...prev, ...defaults }))
    // Persist the defaults too — clearing localStorage alone would leave the
    // stored server values to win again on the next mount.
    onPersistRef.current?.(defaults)
    // data-* + OS-listener reconciliation is handled by the declarative effect.
  }, [])

  const value: DisplayPreferencesContextValue = { prefs, set, reset }

  return (
    <DisplayPreferencesContext.Provider value={value}>
      {children}
    </DisplayPreferencesContext.Provider>
  )
}
